import { createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

import { v4 as uuid } from 'uuid';

import { ConsentsPayload } from 'src/api/zrm';
import parseDate from 'src/utils/lodashLike/parseDate';

import { useApi } from '../APIContext';
import { useCustomerDataContext } from '../auth/CustomerDataContext';

export interface TestConsent {
  hasConsent: boolean;
  lastSeen: Date;
}

export interface MarketingConsentContextProps {
  showConstentBox: boolean;
  closeConsent: (consentChecked: boolean) => void;
  saveConsent: (consentChecked: boolean) => void;
  setConsentWasShownWithUserDetailsForm: Dispatch<SetStateAction<boolean>>;
}

const MarketingConsentContext = createContext<MarketingConsentContextProps>({ showConstentBox: false, closeConsent: () => { }, saveConsent: () => { }, setConsentWasShownWithUserDetailsForm: () => { } });

/**
 * Marketing consent logic explained:
 * * Used only in GR First and EV First flow
 * * Can be saved after user logs in with BankID
 * 
 * Customer should see the consent box in 2 scenarios:
 * 1. When loggin in with BankID to see GR Credits or EV Valuation
 * 2. After loggin in BUT only if consent was not checked in login page & 30 days have passed since last popup shown
 * 
 */
const MarketingConsentContextProvider: FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const { api } = useApi();
  const { customerData } = useCustomerDataContext();

  const [consentInfo, setConsentInfo] = useState<TestConsent>({ hasConsent: true, lastSeen: new Date() });
  const [consentWasShownWithUserDetailsForm, setConsentWasShownWithUserDetailsForm] = useState(false);

  const saveConsent = useMemo(() => async (consentChecked: boolean) => {
    const requestId = uuid();

    const payload: ConsentsPayload = consentChecked ? ({ consents: { customer_club_marketing: true, email_marketing: true, sms_marketing: true } }) : ({ dismissed_consents_popup: true });

    try {
      await api.myPages.upsertMarketingConsentMyPagesMarketingConsentsPost(
        payload, { headers: { 'X-Request-ID': requestId }, cancelToken: requestId },
      );
    } catch (error) {

    }
  }, [api]);

  const closeConsent = useMemo(() => (consentChecked: boolean) => {
    setConsentInfo({ hasConsent: consentChecked, lastSeen: new Date() });

    saveConsent(consentChecked);
  }, [saveConsent]);

  useEffect(() => {
    if (customerData) {
      const hasConsent = customerData?.consents && customerData.consents.customer_club_marketing && customerData.consents.email_marketing && customerData.consents.sms_marketing;

      setConsentInfo({
        hasConsent: hasConsent,
        lastSeen: customerData?.consents_popup_dismissed_timestamp ? parseDate(customerData?.consents_popup_dismissed_timestamp).toDate() : null,
      });
    }
  }, [customerData]);

  const showConstentBox = useMemo(() => {
    // User has consent already
    if (consentInfo.hasConsent) { return false; }

    // Consent popup was shown less than 30 days ago
    if (consentInfo.lastSeen && parseDate().diff(parseDate(consentInfo.lastSeen), 'days') < 30) { return false; }

    // There was marketing consent checkbox shown in user details form
    if (consentWasShownWithUserDetailsForm) { return false; }

    return true;
  }, [consentInfo, consentWasShownWithUserDetailsForm]);

  return (
    <MarketingConsentContext.Provider
      value={{
        showConstentBox, closeConsent, saveConsent, setConsentWasShownWithUserDetailsForm,
      }}
    >
      {children}
    </MarketingConsentContext.Provider>
  );
};

const useMarketingConsentContext = () => useContext(MarketingConsentContext);

export { MarketingConsentContextProvider, useMarketingConsentContext };
